import { useMemo } from 'react';
import type * as CSS from 'csstype';
import { observer } from 'mobx-react-lite';
import { ParticipantPresenterProps } from 'features/ParticipantItem/types';
import { useParticipant } from 'core';
import { ReactionIcon } from 'features/Reactions';
import { selectParticipantReaction } from 'state/selectors';
import { isMainModeratorParticipant, isModeratorParticipant } from 'useCase';
import { isParticipantSpeaker } from 'useCase/webinar/webinarStatus';
import { OrientationEnum, useParticipantItem } from '../../hooks/useParticipantItem';
import { ParticipantBar } from '../../components/ParticipantBar';
import { ParticipantVideo } from '../../components/ParticipantVideo';
import { SpeakerPlaceholder, SpeakerVideoRenderer } from './view';

export const SpeakerVCS = observer(
    ({ isAudioMuted, isVideoEnabled, isShowPulseAnimation, participant, onSizeChanged }: ParticipantPresenterProps) => {
        const { cameraPublication, isLocal } = useParticipant(participant);
        const { getDisplayData, getObjectFit } = useParticipantItem();
        const objectFit: CSS.Properties['objectFit'] = getObjectFit(
            OrientationEnum.Landscape,
            cameraPublication?.dimensions,
        );
        const { currentName, placeholder } = useMemo(
            () => getDisplayData(participant, isLocal),
            [participant, isLocal],
        );
        const track = cameraPublication?.track;
        const isShowVideo = isVideoEnabled && track;

        const reaction = selectParticipantReaction(participant);
        const isModerator = isModeratorParticipant(participant);
        const isMainModerator = isMainModeratorParticipant(participant);
        const isSpeaker = isParticipantSpeaker(participant);

        return (
            <>
                <ParticipantVideo
                    track={track}
                    isLocal={isLocal}
                    withSpeakerPulseAnimation={isShowPulseAnimation}
                    isShowVideo={Boolean(isShowVideo)}
                    cameraPublication={cameraPublication}
                    participant={participant}
                    objectFit={objectFit}
                    placeholder={placeholder}
                    onSizeChanged={onSizeChanged}
                    VideoRenderer={SpeakerVideoRenderer}
                    Placeholder={SpeakerPlaceholder}
                />
                <ParticipantBar
                    isAudioMuted={isAudioMuted}
                    name={currentName}
                    isModerator={isModerator}
                    isMainModerator={isMainModerator}
                    isSpeaker={isSpeaker}
                />
                {reaction && <ReactionIcon reaction={reaction} />}
            </>
        );
    },
);
